import React, { Dispatch, FC, ReactNode, SetStateAction, useEffect, useMemo, createContext, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';

import LoaderContent from 'components/UI/LoaderContent';
import SmallCard from 'components/List/SmallCardV2';
import Avatar from 'components/UI/Avatar';
import IconButton from 'components/UI/CardFooterMenu/IconButton';
import ReactionQuickButton from 'components/Reactions/ReactionQuickButton';
import ReactionMenu from 'components/Reactions/ReactionMenu';
import ReactionsModal from 'components/Reactions/ReactionsModal';
import { ReactionsProvider, useReactionContextState } from 'components/Reactions/hooks';
import { useReactionMenu } from 'components/Reactions/helpers/hooks';
import { useIsUserAllowedToReact } from 'components/Card/helpers/hooks';

import { getSearchCardTestId } from 'constants/aqa/search';
import { CardSchema, MessageType } from 'constants/graphqlTypes';
import { COLORS } from 'styles/constants';
import { getListCreatedDateFormat } from 'helpers/getDateFormat';
import usePageType from 'helpers/usePageType';
import getContrastingTextColor from 'helpers/getContrastingTextColor';

import useOpenModal from 'helpers/useOpenModal';
import useReferrerId from 'helpers/useReferrerId';
import { transformRoute } from 'helpers/routingHelper';
import { ROUTE_LIST_EDIT_DESCRIPTION, ROUTE_OFF_PLATFORM_SHARING, ROUTE_SELECT_CHAT_MODAL } from 'routes';
import { ESearchParams } from 'constants/common';
import { ReactComponent as PaperPlaneIcon } from 'assets/icons/PaperPlane.svg';
import { ReactComponent as ShareIcon } from 'assets/icons/Share.svg';
import {
  StyledGrid,
  StyledListItem,
  StyledFirstListItem,
  StyledGradientOverlay,
  StyledListItemInner,
  StyledListItemHeader,
  StyledOwnerRow,
  StyledOwnerAvatar,
  StyledOwnerDescription,
  StyledListActions,
  StyledReactionButtonWrapper,
} from './styled';
import ProfilePrivacyButtons from '../ProfilePrivacyButtons/ProfilePrivacyButtons';

interface ReactionHandlersContextType {
  setReactionCoordinates?: (coords: { x: number; y: number }) => void;
  setIsQuickReactionOpen?: Dispatch<SetStateAction<boolean>>;
}

const ReactionHandlersContext = createContext<ReactionHandlersContextType>({});

interface OwnerRowProps {
  card: CardSchema;
  isDarkTheme: boolean;
  /** Profile background color for contrast calculation (first item only) */
  bgColor?: string | null;
  showReactionButton?: boolean;
  setReactionCoordinates?: (coords: { x: number; y: number }) => void;
  setIsQuickReactionOpen?: Dispatch<SetStateAction<boolean>>;
  onDescriptionClick?: () => void;
}

const OwnerRow: FC<OwnerRowProps> = ({
  card,
  isDarkTheme,
  bgColor,
  showReactionButton = false,
  setReactionCoordinates: propSetReactionCoordinates,
  setIsQuickReactionOpen: propSetIsQuickReactionOpen,
  onDescriptionClick,
}) => {
  const owner = card.creators?.items?.[0]?.user;
  const description = card.description?.trim();
  const isUserAllowedToReact = useIsUserAllowedToReact(owner);
  const contextHandlers = useContext(ReactionHandlersContext);

  const setReactionCoordinates = propSetReactionCoordinates ?? contextHandlers.setReactionCoordinates;
  const setIsQuickReactionOpen = propSetIsQuickReactionOpen ?? contextHandlers.setIsQuickReactionOpen;

  const { createdAt } = card;
  const fallbackText = createdAt ? `Created ${getListCreatedDateFormat(createdAt)}` : null;

  if (!owner && !description && !fallbackText && !showReactionButton) {
    return null;
  }

  // When bgColor is provided (first item), contrast against the profile background color.
  // Otherwise (remaining items), use theme-based colors.
  const themeRowColor = isDarkTheme ? COLORS.white[100] : COLORS.brown.dark[100];
  const rowColor = bgColor ? getContrastingTextColor(bgColor) : themeRowColor;
  const reactionIsDark = bgColor ? rowColor === COLORS.white[100] : isDarkTheme;

  const descriptionText = description ? <>&ldquo;{description}&rdquo;</> : null;
  const displayText = descriptionText ?? (fallbackText ? <>{fallbackText}</> : null);

  return (
    <StyledOwnerRow style={{ color: rowColor }} showReactionButton={showReactionButton}>
      {owner ? (
        <StyledOwnerAvatar>
          <Avatar user={owner} dimension={20} />
        </StyledOwnerAvatar>
      ) : (
        <StyledOwnerAvatar />
      )}
      {displayText ? (
        <StyledOwnerDescription
          onClick={
            onDescriptionClick
              ? (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onDescriptionClick();
                }
              : undefined
          }
          style={onDescriptionClick ? { cursor: 'pointer' } : undefined}
        >
          {displayText}
        </StyledOwnerDescription>
      ) : null}
      {showReactionButton && isUserAllowedToReact && setReactionCoordinates && setIsQuickReactionOpen && (
        <StyledReactionButtonWrapper>
          <ReactionQuickButton
            entityId={card.id}
            isDark={reactionIsDark}
            isReactionButtonMenuShown={false}
            setIsReactionButtonMenuShown={setIsQuickReactionOpen}
            setReactionCoordinates={setReactionCoordinates}
          />
        </StyledReactionButtonWrapper>
      )}
    </StyledOwnerRow>
  );
};

interface ProfileGridProps {
  items: CardSchema[];
  loadMore: () => void;
  loading: boolean;
  isDark: boolean;
  pageTestId?: string;
  gradientStartColor?: string | null;
  picksTotalCount?: number;
  showPublicLists?: boolean;
  setShowPublicLists?: Dispatch<SetStateAction<boolean>>;
  isListCard?: boolean;
}

interface ProfileListCardProps {
  card: CardSchema;
  dataTestId?: string;
  header?: ReactNode;
  isDark: boolean;
  gradientStartColor?: string | null;
  withGradient?: boolean;
  showReactionButton?: boolean;
  isListCard?: boolean;
}

const ProfileListCard: FC<ProfileListCardProps> = ({
  card,
  dataTestId,
  header,
  isDark,
  gradientStartColor,
  withGradient,
  showReactionButton = false,
  isListCard = false,
}) => {
  const { isMyProfile } = usePageType();
  const { push } = useHistory();
  const referrerId = useReferrerId();
  const Container = withGradient ? StyledFirstListItem : StyledListItem;

  const {
    reactionCoordinates,
    setReactionCoordinates,
    isQuickReactionOpen,
    setIsQuickReactionOpen,
    isReactionsModalOpen,
    setIsReactionsModalOpen,
    infoRef,
  } = useReactionMenu();

  const {
    setPickerId,
    pickerId,
    cardId,
    setCardId,
    setThoughtId,
    thoughtId,
    user,
    setUser,
  } = useReactionContextState();

  const listOwner = card.creators?.items?.[0]?.user;

  useEffect(() => {
    if (card && listOwner) {
      setCardId(card.id);
      setPickerId(listOwner.id ?? referrerId ?? null);
      setUser(listOwner);
    }
  }, [card, listOwner, referrerId, setCardId, setPickerId, setUser]);

  const reactionContextValue = useMemo(
    () => ({
      isQuickReactionOpen,
      setThoughtId,
      thoughtId,
      setPickerId,
      pickerId,
      cardId,
      setCardId,
      user,
      setUser,
    }),
    [isQuickReactionOpen, setThoughtId, thoughtId, setPickerId, pickerId, cardId, setCardId, user, setUser]
  );

  const actionColor = isDark ? COLORS.white[100] : COLORS.brown.dark[100];

  const goToChatSelect = useOpenModal(ROUTE_SELECT_CHAT_MODAL, false, {
    entityId: card.id,
    messageType: MessageType.List,
    collectionId: card.isPrivateList && card.isCollaborative ? card.id : null,
    profileGradientStart: gradientStartColor ?? null,
    profileGradientEnd: gradientStartColor ?? null,
  });

  const handleSendList = () => {
    goToChatSelect();
  };

  const handleShareList = () => {
    push({
      pathname: ROUTE_OFF_PLATFORM_SHARING,
      search: `?${ESearchParams.Type}=${MessageType.List}&${ESearchParams.Id}=${card.id}`,
    });
  };

  return (
    <ReactionsProvider value={reactionContextValue}>
      <ReactionHandlersContext.Provider
        value={{
          setReactionCoordinates: showReactionButton ? setReactionCoordinates : undefined,
          setIsQuickReactionOpen: showReactionButton ? setIsQuickReactionOpen : undefined,
        }}
      >
        <Container ref={infoRef}>
          {withGradient && gradientStartColor && (
            <StyledGradientOverlay isMyProfile={isMyProfile} isDark={isDark} gradientStartColor={gradientStartColor} />
          )}
          <StyledListItemInner>
            {header}
            <SmallCard
              dataTestId={dataTestId}
              item={card}
              disableLink={false}
              coverVariant="stacked"
              isListCard={isListCard}
              isDark
            />
          </StyledListItemInner>
          {!card.isPrivateList && (
            <StyledListActions>
              <IconButton ariaLabel="Send list" color={actionColor} handleClick={handleSendList}>
                <PaperPlaneIcon width={24} height={24} />
              </IconButton>
              <IconButton ariaLabel="Share list" color={actionColor} handleClick={handleShareList}>
                <ShareIcon width={24} height={24} />
              </IconButton>
            </StyledListActions>
          )}
          {reactionCoordinates && reactionCoordinates.x && reactionCoordinates.y && showReactionButton && (
            <ReactionMenu
              isOpen={isQuickReactionOpen}
              anchorPoint={reactionCoordinates}
              onClose={() => setIsQuickReactionOpen(false)}
              onSend={() => {}}
              onEmoji={() => {}}
              onOpenReactionsModal={() => setIsReactionsModalOpen(true)}
            />
          )}
          {isReactionsModalOpen && showReactionButton && (
            <ReactionsModal id={card.id} handleClose={() => setIsReactionsModalOpen(false)} />
          )}
        </Container>
      </ReactionHandlersContext.Provider>
    </ReactionsProvider>
  );
};

interface RemainingListCardProps {
  card: CardSchema;
  dataTestId?: string;
  isDark: boolean;
  showReactionButton: boolean;
  isMyProfile: boolean;
  isListCard?: boolean;
}

const RemainingListCard: FC<RemainingListCardProps> = ({
  card,
  dataTestId,
  isDark,
  showReactionButton,
  isMyProfile,
  isListCard,
}) => {
  const editDescription = useOpenModal(transformRoute(ROUTE_LIST_EDIT_DESCRIPTION, { listId: card.id }), false, {
    description: card.description ?? '',
    isSingleModal: true,
    listCardId: card.cardId,
  });

  return (
    <ProfileListCard
      card={card}
      dataTestId={dataTestId}
      header={
        <OwnerRow
          card={card}
          isDarkTheme={isDark}
          showReactionButton={showReactionButton}
          onDescriptionClick={isMyProfile ? editDescription : undefined}
        />
      }
      isDark={isDark}
      showReactionButton={showReactionButton}
      isListCard={isListCard}
    />
  );
};

const ProfileGrid: FC<ProfileGridProps> = ({
  items,
  loadMore,
  loading,
  isDark,
  pageTestId,
  gradientStartColor,
  picksTotalCount,
  showPublicLists,
  setShowPublicLists,
  isListCard,
}) => {
  const [firstItem, ...remainingItems] = items;

  const { isMyProfile } = usePageType();

  const showReactionButtonForFirst = firstItem && !isMyProfile;
  const showReactionButtonForRemaining = !isMyProfile;

  const firstItemDescriptionClick = useOpenModal(
    transformRoute(ROUTE_LIST_EDIT_DESCRIPTION, { listId: firstItem?.id ?? '' }),
    false,
    {
      description: firstItem?.description ?? '',
      isSingleModal: true,
      listCardId: firstItem?.cardId,
    }
  );

  const firstItemOwnerRow = firstItem ? (
    <OwnerRow
      card={firstItem}
      isDarkTheme={isDark}
      bgColor={gradientStartColor}
      showReactionButton={showReactionButtonForFirst}
      onDescriptionClick={isMyProfile ? firstItemDescriptionClick : undefined}
    />
  ) : null;

  let firstItemHeader: ReactNode;

  if (!firstItemOwnerRow && !showReactionButtonForFirst) {
    firstItemHeader = null;
  } else if (
    firstItem &&
    typeof picksTotalCount === 'number' &&
    typeof showPublicLists === 'boolean' &&
    setShowPublicLists
  ) {
    firstItemHeader = (
      <>
        <StyledListItemHeader>
          <ProfilePrivacyButtons
            picksTotalCount={picksTotalCount}
            showPublicLists={showPublicLists}
            setShowPublicLists={setShowPublicLists}
          />
        </StyledListItemHeader>
        {firstItemOwnerRow}
      </>
    );
  } else {
    firstItemHeader = firstItemOwnerRow;
  }

  return (
    <StyledGrid>
      {firstItem && (
        <ProfileListCard
          key={firstItem.id}
          card={firstItem}
          dataTestId={getSearchCardTestId(0, pageTestId)}
          header={firstItemHeader}
          isDark={isDark}
          gradientStartColor={gradientStartColor}
          withGradient
          showReactionButton={showReactionButtonForFirst}
          isListCard={isListCard}
        />
      )}

      {remainingItems.map((item, index) => (
        <RemainingListCard
          key={item.id}
          card={item}
          dataTestId={getSearchCardTestId(index + 1, pageTestId)}
          isDark={isDark}
          showReactionButton={showReactionButtonForRemaining}
          isMyProfile={isMyProfile}
          isListCard={isListCard}
        />
      ))}

      {loading ? <LoaderContent isFullScreen={!items.length} isDark={isDark} /> : <Waypoint onEnter={loadMore} />}
    </StyledGrid>
  );
};

export default ProfileGrid;
